import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { getEmbedSourceUrl, isEmbed } from '@/config/embed';
import { detectBrowser, detectInAppBrowser, detectOs, getOsVersion } from '@/utils/detectOs';
import { buildFeedbackFields, type FeedbackFields } from '@/utils/feedbackMetadata';

const APP_VERSION = import.meta.env.VITE_BUILD_SHA?.slice(0, 7) || 'dev';

const hostnameOf = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
};

/**
 * Client metadata for the feedback Typeforms' hidden fields. Null until the OS
 * version lookup and auth bootstrap first settle, so a form never mounts
 * without them — consumers read the fields once, at mount, and a signed-in
 * voter's `user_id` only arrives once the profile has loaded. After that it
 * stays non-null: an in-page sign-in re-enters auth initialization, and going
 * back to null would unmount an open form and wipe what the voter typed.
 */
export function useFeedbackMetadata(): FeedbackFields | null {
  const { user, isInitializing } = useAuth();
  const userId = user?.user_id ?? user?.id;
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [osVersion, setOsVersion] = useState<string | undefined | null>(null);

  useEffect(() => {
    let active = true;
    void getOsVersion().then((version) => {
      if (active) setOsVersion(version);
    });
    return () => {
      active = false;
    };
  }, []);

  const fields = useMemo(() => {
    if (osVersion === null || isInitializing) return null;
    const browser = detectBrowser();
    return buildFeedbackFields({
      client: isEmbed() ? 'embed_widget' : 'web',
      os: detectOs(),
      os_version: osVersion,
      browser: browser.name,
      browser_version: browser.version,
      in_app_browser: detectInAppBrowser(),
      app_version: APP_VERSION,
      locale,
      page: pathname,
      partner_host: hostnameOf(getEmbedSourceUrl()),
      user_id: userId,
    });
  }, [osVersion, isInitializing, locale, pathname, userId]);

  const [lastFields, setLastFields] = useState<FeedbackFields | null>(null);
  if (fields !== null && fields !== lastFields) setLastFields(fields);
  return fields ?? lastFields;
}
